import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Package, Truck, CheckCircle2, XCircle, Printer, X, Download } from 'lucide-react';
import api from '../../utils/api';

// Professional Dispatch Slip Component (A5 size - 148mm x 210mm)
const DispatchSlip = React.forwardRef(({ order }, ref) => {
  if (!order) return null;
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const orderId = order.id?.slice(0, 8).toUpperCase() || 'N/A';
  const isPaid = order.payment_status === 'paid';

  return (
    <div 
      ref={ref}
      id="dispatch-slip-content"
      style={{ 
        width: '148mm', 
        minHeight: '210mm',
        padding: '8mm',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '11px',
        lineHeight: '1.4',
        color: '#1a1a1a',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        borderBottom: '3px solid #f97316',
        paddingBottom: '12px',
        marginBottom: '12px',
      }}>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f97316', letterSpacing: '-0.5px' }}>
            Tiffin & Bag
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
            Premium Kids' Lunchboxes & School Bags
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a' }}>
            DISPATCH SLIP
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            #{orderId}
          </div>
        </div>
      </div>

      {/* Order Info Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        backgroundColor: '#f8f9fa',
        padding: '10px 12px',
        borderRadius: '6px',
        marginBottom: '12px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order Date</div>
          <div style={{ fontSize: '12px', fontWeight: '600', marginTop: '2px' }}>{formatDate(order.created_at)}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>{formatTime(order.created_at)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order ID</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace', marginTop: '2px' }}>#{orderId}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment</div>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 'bold', 
            marginTop: '2px',
            color: isPaid ? '#16a34a' : '#ea580c',
            padding: '2px 8px',
            backgroundColor: isPaid ? '#dcfce7' : '#fff7ed',
            borderRadius: '4px',
            display: 'inline-block',
          }}>
            {isPaid ? '✓ PAID' : 'COD'}
          </div>
        </div>
      </div>

      {/* Shipping Address - Prominent */}
      <div style={{ 
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '14px',
        marginBottom: '12px',
        backgroundColor: '#fafafa',
      }}>
        <div style={{ 
          fontSize: '10px', 
          color: '#666', 
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          marginBottom: '8px',
          fontWeight: '600',
        }}>
          📦 Ship To:
        </div>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '6px' }}>
          {order.delivery_address?.name || 'N/A'}
        </div>
        <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
          {order.delivery_address?.line1 || ''}
          {order.delivery_address?.line2 && <><br/>{order.delivery_address.line2}</>}
          <br/>
          {order.delivery_address?.city}, {order.delivery_address?.state}
        </div>
        <div style={{ 
          fontSize: '18px', 
          fontWeight: 'bold', 
          marginTop: '8px',
          backgroundColor: '#fef3c7',
          padding: '6px 10px',
          borderRadius: '4px',
          display: 'inline-block',
        }}>
          PIN: {order.delivery_address?.pincode || 'N/A'}
        </div>
        <div style={{ fontSize: '12px', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
          <strong>📞 Phone:</strong> {order.delivery_address?.phone || order.phone || 'N/A'}
        </div>
      </div>

      {/* Order Items Table */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ 
          fontSize: '10px', 
          color: '#666', 
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          marginBottom: '8px',
          fontWeight: '600',
        }}>
          🛍️ Items ({order.items?.length || 0}):
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', width: '50px' }}>Image</th>
              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Product</th>
              <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: '40px' }}>Qty</th>
              <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', width: '60px' }}>Price</th>
              <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', width: '70px' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.title}
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                    />
                  ) : (
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      📦
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px', verticalAlign: 'middle' }}>
                  <div style={{ fontWeight: '500' }}>{item.title}</div>
                  {item.product_id && (
                    <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>SKU: {item.product_id}</div>
                  )}
                </td>
                <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle', fontWeight: '600' }}>
                  {item.quantity}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'middle' }}>
                  ₹{item.price}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'middle', fontWeight: '600' }}>
                  ₹{(item.price * item.quantity).toFixed(0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div style={{ 
        backgroundColor: '#1f2937',
        color: '#ffffff',
        padding: '12px 14px',
        borderRadius: '6px',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '11px', opacity: '0.8' }}>TOTAL AMOUNT</div>
            {!isPaid && (
              <div style={{ fontSize: '10px', color: '#fbbf24', marginTop: '2px' }}>
                💵 Collect on delivery
              </div>
            )}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            ₹{order.total_amount?.toFixed(0) || '0'}
          </div>
        </div>
      </div>

      {/* Tracking Info (if available) */}
      {order.tracking_number && (
        <div style={{ 
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          padding: '10px 12px',
          borderRadius: '6px',
          marginBottom: '12px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>Tracking Number</div>
              <div style={{ fontSize: '13px', fontWeight: 'bold', fontFamily: 'monospace', marginTop: '2px' }}>
                {order.tracking_number}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>Courier</div>
              <div style={{ fontSize: '12px', fontWeight: '500', marginTop: '2px' }}>
                {order.courier_name || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        borderTop: '1px solid #e5e7eb',
        paddingTop: '10px',
        marginTop: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666' }}>
          <div>Thank you for shopping with us! 🙏</div>
          <div style={{ fontWeight: '500' }}>mothering.in</div>
        </div>
        <div style={{ 
          textAlign: 'center', 
          fontSize: '9px', 
          color: '#9ca3af',
          marginTop: '8px',
          padding: '6px',
          backgroundColor: '#f9fafb',
          borderRadius: '4px',
        }}>
          For queries: support@mothering.in | WhatsApp: +91 98765 43210
        </div>
      </div>

      {/* Cut Line */}
      <div style={{ 
        marginTop: '14px',
        borderTop: '2px dashed #d1d5db',
        paddingTop: '8px',
        textAlign: 'center',
        fontSize: '10px',
        color: '#9ca3af',
      }}>
        ✂️ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      </div>
    </div>
  );
});

DispatchSlip.displayName = 'DispatchSlip';

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [filter, setFilter] = useState('all');
  const [showDispatchSlip, setShowDispatchSlip] = useState(false);
  const printRef = useRef();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetOrders(token);
      setOrders(data.orders || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await api.adminUpdateOrderStatus(orderId, newStatus, token);
      toast.success('Order status updated!');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddTracking = async () => {
    if (!trackingNumber || !courierName) {
      toast.error('Please enter tracking number and courier name');
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${selectedOrder.id}/tracking`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tracking_number: trackingNumber,
          courier_name: courierName,
        }),
      });

      if (response.ok) {
        toast.success('Tracking information added!');
        fetchOrders();
        setTrackingNumber('');
        setCourierName('');
      } else {
        toast.error('Failed to add tracking');
      }
    } catch (error) {
      toast.error('Error adding tracking');
    } finally {
      setUpdating(false);
    }
  };

  const handlePrintDispatchSlip = () => {
    setShowDispatchSlip(true);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('dispatch-slip-content');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dispatch Slip - Order #${selectedOrder?.id?.slice(0, 8).toUpperCase()}</title>
        <style>
          @page {
            size: A5;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        ${printContent.outerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Package className="w-5 h-5" />;
      case 'confirmed':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'shipped':
        return <Truck className="w-5 h-5" />;
      case 'delivered':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      default:
        return <XCircle className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.order_status === filter;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Order Management</h1>
            <p className="text-muted-foreground">Manage and track all orders</p>
          </div>
          <Button onClick={fetchOrders} variant="outline">
            Refresh
          </Button>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All Orders</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
            <TabsTrigger value="shipped">Shipped</TabsTrigger>
            <TabsTrigger value="delivered">Delivered</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No orders found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map((order) => (
              <Card
                key={order.id}
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(order.order_status)}
                      <h3 className="font-semibold">Order #{order.id.slice(0, 8)}</h3>
                      <Badge className={getStatusColor(order.payment_status)}>
                        {order.payment_status}
                      </Badge>
                      <Badge className={getStatusColor(order.order_status)}>
                        {order.order_status}
                      </Badge>
                    </div>
                    
                    {/* Order Items Preview with Images */}
                    <div className="flex gap-2 mt-3 mb-3 overflow-x-auto pb-2">
                      {order.items?.slice(0, 4).map((item, idx) => (
                        <div key={idx} className="flex-shrink-0 flex items-center gap-2 bg-gray-50 rounded-lg p-2 pr-3">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate max-w-[120px]">{item.title}</p>
                            <p className="text-xs text-gray-500">×{item.quantity}</p>
                          </div>
                        </div>
                      ))}
                      {order.items?.length > 4 && (
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-medium text-gray-500">
                          +{order.items.length - 4}
                        </div>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Customer</p>
                        <p className="font-medium">{order.delivery_address?.name || order.phone}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Items</p>
                        <p className="font-medium">{order.items?.length} items</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Amount</p>
                        <p className="font-medium text-lg">₹{order.total_amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>Order Details - #{selectedOrder.id.slice(0, 8)}</DialogTitle>
                  <Button 
                    onClick={handlePrintDispatchSlip}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Dispatch Slip
                  </Button>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Order Status */}
                <div>
                  <Label>Order Status</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'confirmed' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'confirmed')}
                      disabled={updating}
                    >
                      Confirmed
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'shipped' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'shipped')}
                      disabled={updating}
                    >
                      Shipped
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'delivered' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'delivered')}
                      disabled={updating}
                    >
                      Delivered
                    </Button>
                  </div>
                </div>

                {/* Shipping Details */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Shipping Address</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Name:</strong> {selectedOrder.delivery_address?.name}</p>
                    <p><strong>Phone:</strong> {selectedOrder.delivery_address?.phone || selectedOrder.phone}</p>
                    <p><strong>Address:</strong> {selectedOrder.delivery_address?.line1}</p>
                    {selectedOrder.delivery_address?.line2 && (
                      <p><strong>Address 2:</strong> {selectedOrder.delivery_address?.line2}</p>
                    )}
                    <p><strong>City:</strong> {selectedOrder.delivery_address?.city}</p>
                    <p><strong>State:</strong> {selectedOrder.delivery_address?.state}</p>
                    <p><strong>Pincode:</strong> {selectedOrder.delivery_address?.pincode}</p>
                  </div>
                </Card>

                {/* Add Tracking */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Tracking Information</h3>
                  {selectedOrder.tracking_number ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm"><strong>Tracking Number:</strong> {selectedOrder.tracking_number}</p>
                      <p className="text-sm"><strong>Courier:</strong> {selectedOrder.courier_name}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Label>Courier Name</Label>
                        <Input
                          value={courierName}
                          onChange={(e) => setCourierName(e.target.value)}
                          placeholder="Blue Dart, Delhivery, etc."
                        />
                      </div>
                      <div>
                        <Label>Tracking Number</Label>
                        <Input
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          placeholder="Enter tracking number"
                        />
                      </div>
                      <Button onClick={handleAddTracking} disabled={updating}>
                        {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Tracking'}
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Order Items with Images */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Order Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 py-3 border-b last:border-0">
                        {/* Product Image */}
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-16 h-16 object-cover rounded-lg border"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Qty: {item.quantity} × ₹{item.price}
                          </p>
                          {item.product_id && (
                            <p className="text-xs text-gray-400">ID: {item.product_id}</p>
                          )}
                        </div>
                        {/* Price */}
                        <p className="font-semibold text-lg">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total</span>
                      <span className="text-xl font-bold">₹{selectedOrder.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Dispatch Slip Modal for Printing */}
        {showDispatchSlip && selectedOrder && (
          <Dialog open={showDispatchSlip} onOpenChange={setShowDispatchSlip}>
            <DialogContent className="max-w-[180mm] p-0 overflow-visible">
              <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="font-semibold">Dispatch Slip</h3>
                  <p className="text-xs text-gray-500">Order #{selectedOrder.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handlePrint} size="sm" className="bg-blue-600 hover:bg-blue-700">
                    <Printer className="w-4 h-4 mr-2" />
                    Print A5
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowDispatchSlip(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="overflow-auto max-h-[75vh] bg-gray-100 p-4 flex justify-center">
                <div className="shadow-xl">
                  <DispatchSlip ref={printRef} order={selectedOrder} />
                </div>
              </div>
              <div className="p-3 border-t bg-gray-50 text-center text-xs text-gray-500">
                Click "Print A5" to open the print dialog with A5 paper size preset
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminOrdersPage;
